import React, { useEffect, useMemo, useRef, useState } from "react";

/**
 * ImageGallery Slider
 * - Filters by area via pill tabs
 * - Slider with arrows & dots
 * - Thumbnail strip (click to jump)
 * - Lightbox with zoom & drag
 */
export default function ImageGallery({ images = [], areas = [] }) {
  const [activeArea, setActiveArea] = useState("All");
  const tabs = ["All", ...areas.filter(Boolean)];
  const list = useMemo(
    () => (activeArea === "All" ? images : images.filter((i) => i.area === activeArea)),
    [images, activeArea]
  );

  const [index, setIndex] = useState(0);
  useEffect(() => setIndex(0), [activeArea]); // reset when filter changes
  const clamp = (i) => (list.length ? Math.max(0, Math.min(list.length - 1, i)) : 0);
  const go = (i) => setIndex(clamp(i));
  const next = () => go(index + 1);
  const prev = () => go(index - 1);

  // Keyboard navigation
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [index, list.length]);

  // Lightbox state
  const [lightbox, setLightbox] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const dragging = useRef(false);
  const last = useRef({ x: 0, y: 0 });

  const openLightbox = (i) => {
    setIndex(i);
    setZoom(1);
    setOffset({ x: 0, y: 0 });
    setLightbox(true);
  };
  const closeLightbox = () => {
    setLightbox(false);
    setZoom(1);
    setOffset({ x: 0, y: 0 });
  };

  // Wheel zoom in lightbox
  const onWheel = (e) => {
    e.preventDefault();
    const delta = -e.deltaY; // up = zoom in
    const step = delta > 0 ? 0.1 : -0.1;
    setZoom((z) => Math.min(4, Math.max(1, +(z + step).toFixed(2))));
  };

  // Drag to pan
  const onDown = (e) => {
    dragging.current = true;
    last.current = { x: e.clientX ?? e.touches?.[0]?.clientX, y: e.clientY ?? e.touches?.[0]?.clientY };
  };
  const onMove = (e) => {
    if (!dragging.current || zoom === 1) return;
    const x = e.clientX ?? e.touches?.[0]?.clientX;
    const y = e.clientY ?? e.touches?.[0]?.clientY;
    const dx = x - last.current.x;
    const dy = y - last.current.y;
    last.current = { x, y };
    setOffset((o) => ({ x: o.x + dx, y: o.y + dy }));
  };
  const onUp = () => (dragging.current = false);

  return (
    <div>
      {/* Area filter tabs */}
      <div className="flex gap-2 mb-4 overflow-x-auto no-scrollbar pr-1">
        {tabs.map((tab) => {
          const active = activeArea === tab;
          return (
            <button
              key={tab}
              onClick={() => setActiveArea(tab)}
              className={[
                "px-3 py-1.5 rounded-full border text-sm shrink-0 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500",
                active
                  ? "bg-teal-500 text-white border-teal-500"
                  : "bg-white text-gray-700 border-gray-200 hover:border-teal-300",
              ].join(" ")}
              aria-pressed={active}
            >
              {tab}
            </button>
          );
        })}
      </div>

      {list.length === 0 ? (
        <div className="text-sm text-gray-500 bg-gray-50 border border-dashed border-gray-200 rounded-xl p-6">
          No images for this filter.
        </div>
      ) : (
        <>
          {/* Slider viewport */}
          <div className="relative w-full overflow-hidden rounded-xl border border-gray-100 bg-gray-50">
            {/* Slides */}
            <div
              className="whitespace-nowrap transition-transform duration-500 ease-out"
              style={{ transform: `translateX(-${index * 100}%)` }}
            >
              {list.map((img, i) => (
                <button
                  key={`${img.imageUrl}-${i}`}
                  className="inline-block align-top w-full aspect-[16/10] relative group"
                  onClick={() => openLightbox(i)}
                  aria-label="Open image"
                >
                  <img
                    src={img.imageUrl}
                    alt={img.area || "Design image"}
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                  {/* hover hint */}
                  <div className="absolute inset-x-0 bottom-0 p-2 text-xs text-white/90 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition">
                    Click to zoom • Area: {img.area}
                  </div>
                </button>
              ))}
            </div>

            {/* Left/Right controls */}
            <button
              onClick={prev}
              className="absolute left-3 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full bg-white/90 border border-gray-200 shadow-sm flex items-center justify-center hover:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500"
              aria-label="Previous image"
              disabled={index === 0}
            >
              <span className={`inline-block rotate-180 ${index === 0 ? "opacity-40" : ""}`}>➜</span>
            </button>
            <button
              onClick={next}
              className="absolute right-3 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full bg-white/90 border border-gray-200 shadow-sm flex items-center justify-center hover:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500"
              aria-label="Next image"
              disabled={index === list.length - 1}
            >
              <span className={`${index === list.length - 1 ? "opacity-40" : ""}`}>➜</span>
            </button>

            {/* Dots */}
            <div className="absolute bottom-2 left-0 right-0 flex items-center justify-center gap-2">
              {list.map((_, i) => (
                <button
                  key={i}
                  onClick={() => go(i)}
                  className={[
                    "h-2.5 w-2.5 rounded-full transition border border-white/70",
                    i === index ? "bg-teal-500" : "bg-white/70 hover:bg-white",
                  ].join(" ")}
                  aria-label={`Go to slide ${i + 1}`}
                />
              ))}
            </div>
          </div>

          {/* Thumbnail strip */}
          <div className="mt-3 flex gap-2 overflow-x-auto no-scrollbar pr-1">
            {list.map((img, i) => {
              const active = i === index;
              return (
                <button
                  key={`thumb-${img.imageUrl}-${i}`}
                  onClick={() => go(i)}
                  className={[
                    "relative h-16 w-24 rounded-lg overflow-hidden border",
                    active ? "border-teal-500 ring-2 ring-teal-200" : "border-gray-200",
                    "shrink-0",
                  ].join(" ")}
                  aria-label={`Open slide ${i + 1}`}
                >
                  <img
                    src={img.imageUrl}
                    alt={img.area || "Design image"}
                    className="h-full w-full object-cover"
                    loading="lazy"
                  />
                  {active ? (
                    <span className="absolute inset-0 ring-2 ring-teal-500 rounded-lg pointer-events-none" />
                  ) : null}
                </button>
              );
            })}
          </div>
        </>
      )}

      {/* Lightbox */}
      {lightbox && list[index] ? (
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={closeLightbox}
          onKeyDown={(e) => e.key === "Escape" && closeLightbox()}
          role="dialog"
          aria-modal="true"
        >
          {/* Controls */}
          <div className="absolute top-4 left-1/2 -translate-x-1/2 flex items-center gap-2 text-xs">
            <span className="px-2 py-1 rounded-full bg-white/90 text-gray-800 border border-gray-200">
              Scroll / pinch to zoom · drag to pan
            </span>
          </div>

          <button
            className="absolute top-4 right-4 px-3 py-1.5 rounded-full bg-white/90 text-gray-800 border border-gray-200 hover:bg-white"
            onClick={(e) => {
              e.stopPropagation();
              closeLightbox();
            }}
            aria-label="Close"
          >
            Close ✕
          </button>

          <div
            className="relative max-h-[85vh] max-w-[90vw] overflow-hidden cursor-grab active:cursor-grabbing"
            onWheel={onWheel}
            onMouseDown={(e) => {
              e.preventDefault();
              onDown(e);
            }}
            onMouseMove={onMove}
            onMouseUp={onUp}
            onMouseLeave={onUp}
            onTouchStart={onDown}
            onTouchMove={onMove}
            onTouchEnd={onUp}
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={list[index].imageUrl}
              alt={list[index].area || "Design image"}
              style={{
                transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoom})`,
                transformOrigin: "center center",
              }}
              className="select-none pointer-events-none object-contain max-h-[85vh] max-w-[90vw]"
            />
          </div>

          {/* zoom controls */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2">
            <button
              className="h-9 w-9 rounded-full bg-white/90 border border-gray-200 flex items-center justify-center text-lg hover:bg-white"
              onClick={(e) => {
                e.stopPropagation();
                setZoom((z) => Math.max(1, +(z - 0.1).toFixed(2)));
              }}
              aria-label="Zoom out"
            >
              −
            </button>
            <span className="text-white text-sm mx-1">{Math.round(zoom * 100)}%</span>
            <button
              className="h-9 w-9 rounded-full bg-white/90 border border-gray-200 flex items-center justify-center text-lg hover:bg-white"
              onClick={(e) => {
                e.stopPropagation();
                setZoom((z) => Math.min(4, +(z + 0.1).toFixed(2)));
              }}
              aria-label="Zoom in"
            >
              +
            </button>
            <button
              className="ml-2 px-3 py-1.5 rounded-full bg-white/90 border border-gray-200 text-gray-800 hover:bg-white"
              onClick={(e) => {
                e.stopPropagation();
                setZoom(1);
                setOffset({ x: 0, y: 0 });
              }}
              aria-label="Reset zoom"
            >
              Reset
            </button>
          </div>

          {/* lightbox arrows */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              prev();
            }}
            className="absolute left-4 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white/90 border border-gray-200 shadow flex items-center justify-center hover:bg-white"
            aria-label="Previous"
            disabled={index === 0}
          >
            <span className={`rotate-180 ${index === 0 ? "opacity-40" : ""}`}>➜</span>
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              next();
            }}
            className="absolute right-4 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white/90 border border-gray-200 shadow flex items-center justify-center hover:bg-white"
            aria-label="Next"
            disabled={index === list.length - 1}
          >
            <span className={`${index === list.length - 1 ? "opacity-40" : ""}`}>➜</span>
          </button>
        </div>
      ) : null}
    </div>
  );
}
